import React from 'react';
import {Box} from '@mui/material';
import {NAVY, TEXTE_SECONDAIRE} from '../../tokens.js';

// Lot 12 : nuage de points SVG maison (aucune bibliothèque de graphiques).
// `points` : [{id, x, y, accent?, titre?}] ; `seuilX` / `seuilY` : lignes de repère pointillées ; `onPoint(id)` rend les points cliquables.

const L = 640;
const H = 300;
const MARGE = {haut: 10, droite: 14, bas: 36, gauche: 54};
const ORANGE = '#F39A3F';

/** Borne supérieure « ronde » d'un maximum (1, 2, 2,5, 5 × 10^n) et 4 graduations. */
export function echelleNuage(max) {
    if (!(max > 0)) return {max: 1, graduations: [0, 0.25, 0.5, 0.75, 1]};
    const p = 10 ** Math.floor(Math.log10(max));
    const haut = [1, 2, 2.5, 5, 10].map((m) => m * p).find((v) => v >= max);
    return {max: haut, graduations: [0, 1, 2, 3, 4].map((i) => (haut * i) / 4)};
}

const nb = (n) => Math.round(n).toLocaleString('fr-FR');

export default function NuagePoints({points, libelleX, libelleY, seuilX = null, seuilY = null, onPoint = null, aria}) {
    if (points.length === 0) {
        return <Box sx={{height: 80, display: 'flex', alignItems: 'center', fontSize: 12, color: TEXTE_SECONDAIRE}}>—</Box>;
    }
    const ex = echelleNuage(Math.max(...points.map((p) => p.x), seuilX || 0));
    const ey = echelleNuage(Math.max(...points.map((p) => p.y), seuilY || 0));
    const px = (x) => MARGE.gauche + (x / ex.max) * (L - MARGE.gauche - MARGE.droite);
    const py = (y) => H - MARGE.bas - (y / ey.max) * (H - MARGE.haut - MARGE.bas);
    // Les points « accentués » sont dessinés en dernier (au-dessus).
    const ordonnes = [...points].sort((a, b) => Number(!!a.accent) - Number(!!b.accent));
    return (
        <Box component="svg" role="img" aria-label={aria} viewBox={`0 0 ${L} ${H}`} width="100%" sx={{display: 'block', maxHeight: 320}}>
            {ey.graduations.map((g) => (
                <g key={`y${g}`}>
                    <line x1={MARGE.gauche} x2={L - MARGE.droite} y1={py(g)} y2={py(g)} stroke="#EEF1F4" strokeWidth="1"/>
                    <text x={MARGE.gauche - 6} y={py(g) + 3} textAnchor="end" fontSize="10" fill={TEXTE_SECONDAIRE}>{nb(g)}</text>
                </g>
            ))}
            {ex.graduations.map((g) => (
                <text key={`x${g}`} x={px(g)} y={H - MARGE.bas + 14} textAnchor="middle" fontSize="10" fill={TEXTE_SECONDAIRE}>{nb(g)}</text>
            ))}
            {seuilX != null && <line data-testid="nuage-seuil-x" x1={px(seuilX)} x2={px(seuilX)} y1={MARGE.haut} y2={H - MARGE.bas} stroke="#8A929C" strokeWidth="1" strokeDasharray="4 3"/>}
            {seuilY != null && <line data-testid="nuage-seuil-y" x1={MARGE.gauche} x2={L - MARGE.droite} y1={py(seuilY)} y2={py(seuilY)} stroke="#8A929C" strokeWidth="1" strokeDasharray="4 3"/>}
            {ordonnes.map((p) => (
                <circle
                    key={p.id}
                    cx={px(p.x)}
                    cy={py(p.y)}
                    r={p.accent ? 4 : 3}
                    fill={p.accent ? ORANGE : NAVY}
                    fillOpacity={p.accent ? 0.95 : 0.45}
                    style={onPoint ? {cursor: 'pointer'} : undefined}
                    onClick={onPoint ? () => onPoint(p.id) : undefined}
                >
                    {p.titre && <title>{p.titre}</title>}
                </circle>
            ))}
            <text x={(MARGE.gauche + L - MARGE.droite) / 2} y={H - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill="#2D333B">{libelleX}</text>
            <text transform={`translate(12 ${(MARGE.haut + H - MARGE.bas) / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fontWeight="600" fill="#2D333B">{libelleY}</text>
        </Box>
    );
}
